import type { BloqueHomeDTO, EstiloBloqueDTO, TipoBloqueHome } from "./types";

export type TipoCampoEditable =
    | "texto"
    | "textarea"
    | "imagen"
    | "imagenes"
    | "numero"
    | "toggle"
    | "opciones";

export interface OpcionCampo {
    valor: string | number;
    label: string;
}

export interface CampoEstilo {
    key: keyof EstiloBloqueDTO;
    label: string;
    tipo: "color" | "numero" | "preset-radio" | "css";
    opciones?: OpcionCampo[];
}

export interface CampoEditable {
    key: string;
    label: string;
    tipo: TipoCampoEditable;
    opciones?: OpcionCampo[];
}

const OPCIONES_CANTIDAD: OpcionCampo[] = Array.from({ length: 10 }, (_, intIndice) => ({
    valor: intIndice + 1,
    label: String(intIndice + 1),
}));

const OPCIONES_VARIANTE_HERO: OpcionCampo[] = [
    { valor: "split", label: "Dividido" },
    { valor: "fullwidth", label: "Ancho completo" },
    { valor: "centrado", label: "Centrado" },
    { valor: "minimal", label: "Minimalista" },
];

const OPCIONES_VARIANTE_CATEGORIAS: OpcionCampo[] = [
    { valor: "banner", label: "Banner destacado" },
    { valor: "mosaico", label: "Mosaico" },
    { valor: "editorial", label: "Editorial" },
];

const CAMPO_ETIQUETA: CampoEditable = {
    key: "vchEyebrow",
    label: "Etiqueta",
    tipo: "texto",
};
const CAMPO_TITULO: CampoEditable = {
    key: "vchTitulo",
    label: "Título",
    tipo: "texto",
};
const CAMPO_SUBTITULO: CampoEditable = {
    key: "vchSubtitulo",
    label: "Subtítulo",
    tipo: "textarea",
};
const CAMPO_TEXTO_BOTON: CampoEditable = {
    key: "vchTextoBoton",
    label: "Texto del botón",
    tipo: "texto",
};
const CAMPO_LINK_BOTON: CampoEditable = {
    key: "vchLinkBoton",
    label: "Enlace del botón",
    tipo: "texto",
};
const CAMPO_IMAGEN: CampoEditable = {
    key: "vchImagen",
    label: "Imagen",
    tipo: "imagen",
};
const CAMPO_CANTIDAD: CampoEditable = {
    key: "intCantidad",
    label: "Cantidad de elementos",
    tipo: "opciones",
    opciones: OPCIONES_CANTIDAD,
};

const CAMPOS_CONTENIDO_POR_TIPO: Record<TipoBloqueHome, CampoEditable[]> = {
    hero: [
        {
            key: "vchVariante",
            label: "Disposición del hero",
            tipo: "opciones",
            opciones: OPCIONES_VARIANTE_HERO,
        },
        CAMPO_ETIQUETA,
        CAMPO_TITULO,
        CAMPO_SUBTITULO,
        CAMPO_TEXTO_BOTON,
        CAMPO_LINK_BOTON,
        {
            key: "vchImagenes",
            label: "Imágenes del hero",
            tipo: "imagenes",
        },
    ],
    categorias: [
        {
            key: "vchVariante",
            label: "Presentación de categorías",
            tipo: "opciones",
            opciones: OPCIONES_VARIANTE_CATEGORIAS,
        },
        CAMPO_ETIQUETA,
        CAMPO_TITULO,
        {
            ...CAMPO_SUBTITULO,
            label: "Descripción de la sección",
        },
        CAMPO_CANTIDAD,
    ],
    productosDestacados: [CAMPO_ETIQUETA, CAMPO_TITULO, CAMPO_CANTIDAD],
    novedades: [CAMPO_ETIQUETA, CAMPO_TITULO, CAMPO_CANTIDAD],
    bannerEditorial: [
        CAMPO_ETIQUETA,
        CAMPO_TITULO,
        CAMPO_SUBTITULO,
        CAMPO_TEXTO_BOTON,
        CAMPO_LINK_BOTON,
        CAMPO_IMAGEN,
    ],
    about: [
        CAMPO_ETIQUETA,
        CAMPO_TITULO,
        CAMPO_SUBTITULO,
        CAMPO_TEXTO_BOTON,
        CAMPO_LINK_BOTON,
        CAMPO_IMAGEN,
    ],
    confianza: [],
    newsletter: [CAMPO_TITULO, CAMPO_SUBTITULO],
};

const CAMPO_CSS_CUSTOM: CampoEstilo = {
    key: "vchCssCustom",
    label: "CSS personalizado",
    tipo: "css",
};

const CAMPOS_ESTILO_BASE: CampoEstilo[] = [
    { key: "vchColorFondo", label: "Fondo de sección", tipo: "color" },
    { key: "vchColorTexto", label: "Color de texto", tipo: "color" },
    CAMPO_CSS_CUSTOM,
];

const CAMPOS_ESTILO_CON_BOTON: CampoEstilo[] = [
    { key: "vchColorFondo", label: "Fondo de sección", tipo: "color" },
    { key: "vchColorTexto", label: "Color de texto", tipo: "color" },
    { key: "vchColorBotonFondo", label: "Color del botón", tipo: "color" },
    { key: "vchColorBotonTexto", label: "Texto del botón", tipo: "color" },
    { key: "intRadioBoton", label: "Radio del botón", tipo: "preset-radio" },
    CAMPO_CSS_CUSTOM,
];

const TIPOS_CON_BOTON = new Set(["hero", "bannerEditorial", "about", "newsletter"]);

export function derivarCamposEstilo(
    vchTipo: string,
    opcionesColor: OpcionCampo[] = [],
): CampoEstilo[] {
    const campos = TIPOS_CON_BOTON.has(vchTipo) ? CAMPOS_ESTILO_CON_BOTON : CAMPOS_ESTILO_BASE;
    return campos.map((campo) =>
        campo.tipo === "color" ? { ...campo, opciones: opcionesColor } : campo,
    );
}

export function derivarCamposEditables(
    config: BloqueHomeDTO["config"],
    vchTipo?: TipoBloqueHome,
    bitActivo?: boolean,
): CampoEditable[] {
    let campos = vchTipo
        ? CAMPOS_CONTENIDO_POR_TIPO[vchTipo].map((campo) => ({
              ...campo,
              opciones: campo.opciones ? [...campo.opciones] : undefined,
          }))
        : [];

    if (vchTipo === "categorias" && config.vchVariante !== "banner" && !config.vchSubtitulo) {
        campos = campos.filter((campo) => campo.key !== "vchSubtitulo");
    }

    if (bitActivo !== undefined) {
        campos.unshift({
            key: "bitActivo",
            label: "Mostrar sección",
            tipo: "toggle",
        });
    }

    return campos;
}
